import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { FaGripVertical, FaTimes } from "react-icons/fa";
import "./DualMontageReviewFloatingPanel.css";

export type DualMontageReviewFloatingPanelProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional anchor; defaults to the Dual Compare workspace control. */
  anchorSelector?: string;
};

const PANEL_MARGIN_PX = 8;
const FALLBACK_WIDTH_PX = 720;

function clampOffset(
  x: number,
  y: number,
  panelWidth: number,
  panelHeight: number,
): { x: number; y: number } {
  const maxX = Math.max(PANEL_MARGIN_PX, window.innerWidth - panelWidth - PANEL_MARGIN_PX);
  const maxY = Math.max(PANEL_MARGIN_PX, window.innerHeight - panelHeight - PANEL_MARGIN_PX);
  return {
    x: Math.min(Math.max(PANEL_MARGIN_PX, x), maxX),
    y: Math.min(Math.max(PANEL_MARGIN_PX, y), maxY),
  };
}

function resolveAnchorRect(anchorSelector?: string): DOMRect | null {
  const preferred = [
    anchorSelector,
    '.eeg-toolbar-cluster--actions button.eeg-toolbar-filter-toggle',
    "button.eeg-toolbar-filter-toggle",
  ].filter(Boolean) as string[];

  for (const selector of preferred) {
    const nodes = document.querySelectorAll(selector);
    for (const node of nodes) {
      if (!(node instanceof HTMLElement)) continue;
      const label = `${node.textContent || ""} ${node.getAttribute("title") || ""}`;
      if (!/Dual Compare/i.test(label)) continue;
      // Prefer the pressed/active Dual Compare control when present.
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        if (node.getAttribute("aria-pressed") === "true") return rect;
      }
    }
  }

  for (const selector of preferred) {
    const nodes = document.querySelectorAll(selector);
    for (const node of nodes) {
      if (!(node instanceof HTMLElement)) continue;
      const label = `${node.textContent || ""} ${node.getAttribute("title") || ""}`;
      if (!/Dual Compare/i.test(label)) continue;
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return rect;
    }
  }

  return null;
}

function offsetNearAnchor(
  anchor: DOMRect | null,
  panelWidth: number,
  panelHeight: number,
): { x: number; y: number } {
  if (!anchor) {
    return clampOffset(
      window.innerWidth - panelWidth - 16,
      72,
      panelWidth,
      panelHeight,
    );
  }

  // Hang the panel under Dual Compare: prefer right-align to the control.
  // If that would clamp far left of the button, fall back to left-align so
  // the popup still reads as attached to the Dual Compare control.
  const y = anchor.bottom + 8;
  const rightAligned = anchor.right - panelWidth;
  const leftAligned = anchor.left;
  const maxX = Math.max(
    PANEL_MARGIN_PX,
    window.innerWidth - panelWidth - PANEL_MARGIN_PX,
  );
  let x = rightAligned;
  if (x < PANEL_MARGIN_PX) {
    x = Math.min(Math.max(PANEL_MARGIN_PX, leftAligned), maxX);
  }
  return clampOffset(x, y, panelWidth, panelHeight);
}

export const DualMontageReviewFloatingPanel: React.FC<
  DualMontageReviewFloatingPanelProps
> = ({ open, onClose, children, anchorSelector }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const positionedForOpenRef = useRef(false);
  const [offset, setOffset] = useState(() =>
    offsetNearAnchor(null, FALLBACK_WIDTH_PX, 280),
  );
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      positionedForOpenRef.current = false;
      dragRef.current = null;
      return;
    }

    const place = () => {
      const panel = panelRef.current;
      const width = panel?.offsetWidth || FALLBACK_WIDTH_PX;
      const height = panel?.offsetHeight || 280;
      const next = offsetNearAnchor(
        resolveAnchorRect(anchorSelector),
        width,
        height,
      );
      setOffset(next);
      positionedForOpenRef.current = true;
    };

    place();
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(place);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [anchorSelector, open]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: offset.x,
        originY: offset.y,
      };
    },
    [offset.x, offset.y],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const panel = panelRef.current;
      const next = clampOffset(
        drag.originX + (event.clientX - drag.startX),
        drag.originY + (event.clientY - drag.startY),
        panel?.offsetWidth ?? FALLBACK_WIDTH_PX,
        panel?.offsetHeight ?? 280,
      );
      setOffset(next);
    },
    [],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    },
    [],
  );

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="dual-montage-floating"
      style={{ left: offset.x, top: offset.y }}
      role="dialog"
      aria-label="Dual Compare"
      aria-modal="false"
    >
      <div className="dual-montage-floating__header">
        <button
          type="button"
          className="dual-montage-floating__drag"
          aria-label="Drag Dual Compare panel"
          title="Drag Dual Compare panel"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <FaGripVertical aria-hidden="true" />
        </button>
        <strong className="dual-montage-floating__title">Dual Compare</strong>
        <button
          type="button"
          className="dual-montage-floating__close"
          aria-label="Close Dual Compare"
          title="Close Dual Compare"
          onClick={onClose}
        >
          <FaTimes aria-hidden="true" />
        </button>
      </div>
      <div className="dual-montage-floating__body">{children}</div>
    </div>
  );
};

export default DualMontageReviewFloatingPanel;
